import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import { handleAuth0CustomErrorMessage } from '../util/StringUtil';
import { useTranslationData } from '../TranslationContext';
import { persistentToastWarning } from '../util/ToastifyUtil';
import { toast } from 'react-toastify';

/**
 * Custom hook that manages Auth0 access token synchronization and related error handling.
 * This hook handles silent token refresh, error message display, and logout scenarios.
 */
export const useSyncAuth0AccessToken = (clientId: string | undefined) => {
	const { getAccessTokenSilently, error, logout } = useAuth0();
	const { resetCachedSelectedLanguages } = useTranslationData();
	const [isTokenLoading, setIsTokenLoading] = useState(true);

	/**
	 * Effect for silently refreshing the access token
	 * Handles the "Login required" error by resetting cached languages
	 */
	useEffect(() => {
		const runAsync = async () => {
			try {
				// Attempt to get a new access token without using cache
				await getAccessTokenSilently({
					cacheMode: 'off'
				});
			} catch (e: any) {
				// If login is required, reset language preferences
				if (e.message.startsWith('Login required')) {
					resetCachedSelectedLanguages?.();
				}
			} finally {
				setIsTokenLoading(false);
			}
		};

		runAsync();
	}, [getAccessTokenSilently]);

	/**
	 * Effect for handling Auth0 errors
	 * Processes error messages and triggers logout if necessary
	 */
	useEffect(() => {
		if (error) {
			setIsTokenLoading(true);
			let errorMessage = handleAuth0CustomErrorMessage(error.message);
			if (errorMessage != null) {
				localStorage.setItem('login-error-message', errorMessage);
				logout({
					clientId, logoutParams: {
						returnTo: window.location.origin
					}
				});
			} else {
				setIsTokenLoading(false);
			}
		}
	}, [error, logout, clientId]);

	/**
	 * Effect for displaying stored error messages
	 * Shows a persistent toast warning with custom content based on error type
	 */
	useEffect(() => {
		let errorMessage = localStorage.getItem('login-error-message');
		if (errorMessage) {
			let toastContent = <div>{errorMessage}</div>;
			if (errorMessage.startsWith('Your user ID is currently not associated with a Language I/O account')) {
				toastContent = (
					<div>
						{errorMessage}
					<a href="https://languageio.zendesk.com/hc/en-us/requests/new" target="_blank">here</a>
					</div>
			);
			}
			persistentToastWarning(toastContent, () => toast.dismiss('persistentToastWarning'));
			localStorage.removeItem('login-error-message');
		}
	}, []);

	return { isTokenLoading };
};